import React from 'react'

import classnames from 'classnames'

import { LogoSizeOption, Image as ImageObject } from 'src/apollo/sites'
import { ScrollDirection } from 'src/lib/js/hooks/useScrollDirection'

import Image from 'shared/components/common/Image'
import Link from 'shared/components/common/link'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { resources } from 'config'

type Props = {
  /** The URL the logo links to. Pass `null` to render a non-interactive logo. */
  logoLink?: string | null
  logoLinkSameTab?: boolean
  logoSrc: string
  logoObject?: ImageObject | null
  logoSize?: LogoSizeOption | null
  scrollDirection?: ScrollDirection
  style?: React.CSSProperties
  onLoad?: (ev: React.SyntheticEvent) => void
}

const NavLogo = (props: Props) => {
  const { restaurant, host } = useRestaurant()
  const isToastOrderPage = host === resources.toastOrderHost
  const isToastLocalPage = host === resources.toastLocalHost
  const homepage = restaurant?.meta?.homepage

  // Determine logo link destination with clear priority order
  let logoLink: string | null

  if (props.logoLink === null) {
    // Logo should be non-interactive (no link wrapper)
    logoLink = null
  } else if (
    isToastLocalPage ||
    (isToastOrderPage && (!homepage || homepage === '/'))
  ) {
    // On Toast Local or Toast order subdomain without a custom homepage:
    // Use the logoLink prop provided by the parent component
    logoLink = props.logoLink || '/'
  } else {
    // Use custom homepage if configured, otherwise fall back to logoLink prop, or default to '/'
    logoLink = homepage || props.logoLink || '/'
  }

  const overflow =
    props.logoSize === LogoSizeOption.OverflowCircle &&
    (!props.scrollDirection || props.scrollDirection === 'up')

  const logoContent =
    props.logoSrc || props.logoObject ? (
      <div
        className={classnames('logoWrapper', {
          'overflow-circle': overflow
        })}
      >
        <h1 className='hiddenHeader'>{restaurant.name}</h1>
        <Image
          alt=''
          imageObject={props.logoObject}
          src={props.logoSrc}
          className='logo'
          editableComponentName='logo'
          eagerLoad
          onLoad={props.onLoad}
        />
        <div
          className={classnames('backgroundCircle', { hidden: !overflow })}
          style={props.style}
        />
      </div>
    ) : (
      <h1 className={classnames({ smaller: restaurant?.name?.length > 40 })}>
        {restaurant?.name}
      </h1>
    )

  return (
    <div className='logoTitle' data-testid='logoTitle'>
      {logoLink === null ? (
        logoContent
      ) : (
        <Link
          href={logoLink}
          sameTab={props.logoLinkSameTab}
          data-testid='logoLinkId'
          testId='logoLinkId'
          ariaLabel={`${restaurant?.name}`}
        >
          {logoContent}
        </Link>
      )}
    </div>
  )
}

export default NavLogo
